import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LogoutIcon from '@mui/icons-material/Logout';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton, FacebookIcon, TwitterIcon, WhatsappIcon } from 'react-share';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import { useAuth } from '../context/AuthContext';
import { getUserAddress } from '../services/api';
import toast from 'react-hot-toast';
import api from '../services/api';

const Profile = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [userInfo, setUserInfo] = useState(null);
  const [refData, setRefData] = useState([]);
  const [refDialog, setRefDialog] = useState(false);
  const [copyStatus, setCopyStatus] = useState(false);
  console.log("copyStatus",copyStatus);
  const onCopyText = () => {
    setCopyStatus(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopyStatus(false), 2000);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    const fetchUserData = async () => {
      try {
        const userId = localStorage.getItem('UserId');
        const response = await getUserAddress(userId);
        if (response.data.data && response.data.data[0]) {
          setUserInfo(response.data.data[0]);

          // Fetch reference data
          if (response.data.data[0].ReferenceID) {
            const refRes = await api.get(`/getReference/${response.data.data[0].ReferenceID}`);
            if (refRes.data) {
              setRefData(refRes.data.data || []);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { id: 'profile', label: 'My Profile', icon: <PersonIcon /> },
    { id: 'orders', label: 'My Orders', icon: <ShoppingBagIcon />, path: '/orders' },
    { id: 'wishlist', label: 'Wishlist', icon: <FavoriteIcon />, path: '/wishlist' },
    { id: 'addresses', label: 'Addresses', icon: <LocationOnIcon /> },
    { id: 'referrals', label: 'Referrals', icon: <ShareIcon /> },
  ];

  if (loading) {
    return <Loader message="Loading profile..." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumb items={[{ label: 'My Profile' }]} />

        <Typography variant="h4" fontWeight="bold" color="#3E2723" sx={{ mb: 3 }}>
          My Account
        </Typography>

        <Grid container spacing={4}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: '#fff', overflow: 'hidden' }}>
              {/* User Info */}
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#876513', color: '#fff' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: '#fff',
                    color: '#876513',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  {userInfo?.FirstName} {userInfo?.LastName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {userInfo?.Email}
                </Typography>
              </Box>

              {/* Menu */}
              <List>
                {menuItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={activeTab === item.id}
                    onClick={() => {
                      if (item.path) {
                        navigate(item.path);
                      } else {
                        setActiveTab(item.id);
                      }
                    }}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: '#faf8f5',
                        borderLeft: '4px solid #876513',
                        '&:hover': { bgcolor: '#faf8f5' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: activeTab === item.id ? '#876513' : 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
                <Divider />
                <ListItemButton onClick={handleLogout} sx={{ color: '#e53935' }}>
                  <ListItemIcon sx={{ color: '#e53935' }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </List>
            </Paper>
          </Grid>

          {/* Content */}
          <Grid item xs={12} md={9}>
            {activeTab === 'profile' && (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Personal Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={userInfo?.FirstName || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={userInfo?.LastName || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={userInfo?.Email || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mobile"
                      value={userInfo?.Mobile || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Gender"
                      value={userInfo?.Gender || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  sx={{
                    mt: 3,
                    bgcolor: '#876513',
                    '&:hover': { bgcolor: '#6d3610' },
                  }}
                >
                  Edit Profile
                </Button>
              </Paper>
            )}

            {activeTab === 'addresses' && (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Saved Addresses
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {userInfo?.Address ? (
                  <Box
                    sx={{
                      p: 2,
                      border: '2px solid #876513',
                      borderRadius: 2,
                      bgcolor: '#faf8f5',
                      maxWidth: 400,
                    }}
                  >
                    <Typography fontWeight="bold" gutterBottom>
                      Default Address
                    </Typography>
                    <Typography color="text.secondary">
                      {userInfo.Address}
                    </Typography>
                    <Typography color="text.secondary">
                      {userInfo.City}, {userInfo.State}
                    </Typography>
                    <Typography color="text.secondary">
                      Pin Code: {userInfo.PinCode}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <LocationOnIcon sx={{ fontSize: 60, color: '#ddd' }} />
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      No saved addresses
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  sx={{
                    mt: 3,
                    borderColor: '#876513',
                    color: '#876513',
                  }}
                >
                  Add New Address
                </Button>
              </Paper>
            )}

            {activeTab === 'referrals' && (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Your Referral Program
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<ShareIcon />}
                    onClick={() => setRefDialog(true)}
                    sx={{ bgcolor: '#876513', '&:hover': { bgcolor: '#6d3610' } }}
                  >
                    Share & Earn
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ bgcolor: '#faf8f5', p: 3, borderRadius: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Your Referral Code
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" fontWeight="bold" color="#876513">
                      {userInfo?.ReferenceID || 'N/A'}
                    </Typography>
                    <CopyToClipboard text={userInfo?.ReferenceID} onCopy={onCopyText}>
                      <IconButton size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    </CopyToClipboard>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Share this code with friends and earn rewards when they sign up
                  </Typography>
                </Box>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Your Referrals ({refData.length})
                </Typography>

                {refData.length > 0 ? (
                  <List>
                    {refData.map((ref, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          mb: 1,
                          p: 2,
                          border: '1px solid #eee',
                          borderRadius: 2,
                        }}
                      >
                        <Avatar sx={{ bgcolor: '#876513', mr: 2 }}>
                          {ref.FirstName?.charAt(0)}
                        </Avatar>
                        <ListItemText
                          primary={`${ref.FirstName} ${ref.LastName}`}
                          secondary={ref.Email}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ShareIcon sx={{ fontSize: 60, color: '#ddd' }} />
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      No referrals yet. Share your code to start earning!
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Referral Dialog */}
      <Dialog open={refDialog} onClose={() => setRefDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Share Your Referral Code
          <IconButton
            onClick={() => setRefDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" fontWeight="bold" color="#876513" gutterBottom>
              {userInfo?.ReferenceID}
            </Typography>
            <CopyToClipboard text={userInfo?.ReferenceID} onCopy={onCopyText}>
              <Button startIcon={<ContentCopyIcon />} sx={{ mb: 3 }}>
                Copy Code
              </Button>
            </CopyToClipboard>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Share on social media:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <FacebookShareButton
                url={`https://nutnutritions.com/?popupsignup=true&ref=${userInfo?.ReferenceID}`}
                quote={`Use my referral code ${userInfo?.ReferenceID} to get exclusive discounts!`}
              >
                <FacebookIcon size={40} round />
              </FacebookShareButton>
              <TwitterShareButton
                url={`https://nutnutritions.com/?popupsignup=true&ref=${userInfo?.ReferenceID}`}
                title={`Use my referral code ${userInfo?.ReferenceID} to get exclusive discounts!`}
              >
                <TwitterIcon size={40} round />
              </TwitterShareButton>
              <WhatsappShareButton
                url={`https://nutnutritions.com/?popupsignup=true&ref=${userInfo?.ReferenceID}`}
                title={`Use my referral code ${userInfo?.ReferenceID} to get exclusive discounts!`}
              >
                <WhatsappIcon size={40} round />
              </WhatsappShareButton>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default Profile;
